import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

const shuffleWithSeed = (array, seed) => {
  const result = [...array];
  const rand = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  for (let i = result.length - 1; i > 0; i--) {
    seedValue++;
    const j = Math.floor(rand(seedValue) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

const SortingElement = ({
  element,
  isEditor = false,
  studentView = false,
  placements: externalPlacements = null,
  showResult = false,
  perItemResults = null,
  onPlacementsChange,
  onRetry,
  disabled = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    layout = 'baskets',
    categories = [],
    items = [],
    allowRetry = false,
    paletteSide = 'top',
    fontSize = 16,
    fontFamily = 'Inter',
    itemColor = '#ffffff',
    itemTextColor = '#1f2937',
    categoryColor = '#2A4D25',
  } = content || {};
  const backgroundColor = style?.backgroundColor || '#ffffff';

  const shuffledItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 1;
    return shuffleWithSeed(items, seed);
  }, [items, id]);

  const [placements, setPlacements] = useState(externalPlacements || {});
  useEffect(() => {
    if (externalPlacements) setPlacements(externalPlacements);
  }, [externalPlacements]);

  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverCategoryId, setDragOverCategoryId] = useState(null);
  const pointerDragRef = useRef(null);
  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: items.length + categories.length,
    onAutoGrow,
  });

  const placedItemIds = useMemo(
    () => new Set(Object.entries(placements).filter(([, v]) => v).map(([k]) => k)),
    [placements]
  );

  const updatePlacements = useCallback((next) => {
    setPlacements(next);
    onPlacementsChange?.(next);
  }, [onPlacementsChange]);

  const dragEnabled = !disabled && !showResult && !isEditor;

  const startNativeDrag = (e, itemId) => {
    if (!dragEnabled) return;
    const item = items.find((it) => it.id === itemId);
    e.dataTransfer.setData('text/plain', itemId);
    e.dataTransfer.effectAllowed = 'move';

    // Safari can't render drag image inside a `transform: scale(...)` subtree —
    // clone to body so WebKit has something to attach the pointer to.
    if (item && typeof document !== 'undefined' && e.dataTransfer.setDragImage) {
      const ghost = document.createElement('div');
      ghost.textContent = item.text;
      Object.assign(ghost.style, {
        position: 'fixed',
        top: '-1000px',
        left: '-1000px',
        padding: '6px 14px',
        borderRadius: '9999px',
        border: `2px solid ${categoryColor}`,
        background: itemColor,
        color: itemTextColor,
        font: `500 ${fontSize - 2}px "${fontFamily}", Inter, system-ui, sans-serif`,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      });
      document.body.appendChild(ghost);
      const rect = ghost.getBoundingClientRect();
      e.dataTransfer.setDragImage(ghost, rect.width / 2, rect.height / 2);
      setTimeout(() => { ghost.remove(); }, 0);
    }
    setDraggedItemId(itemId);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDragOverCategoryId(null);
  };

  const handleCategoryDragOver = (e, categoryId) => {
    if (!dragEnabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCategoryId(categoryId);
  };

  const handleCategoryDragLeave = () => setDragOverCategoryId(null);

  const handleCategoryDrop = (e, categoryId) => {
    if (!dragEnabled) return;
    e.preventDefault();
    const itemId = draggedItemId || e.dataTransfer.getData('text/plain');
    if (!itemId) return;
    updatePlacements({ ...placements, [itemId]: categoryId });
    setDraggedItemId(null);
    setDragOverCategoryId(null);
  };

  const handlePlacedClick = (itemId) => {
    if (!dragEnabled) return;
    if (!placements[itemId]) return;
    const next = { ...placements };
    next[itemId] = null;
    updatePlacements(next);
  };

  // Pointer-based drag for touch/pen — HTML5 drag doesn't fire on mobile browsers.
  const handlePointerDown = useCallback((e, itemId) => {
    if (!dragEnabled) return;
    if (e.pointerType === 'mouse') return;
    if (pointerDragRef.current) return;
    const item = items.find((it) => it.id === itemId);
    if (!item) return;
    e.preventDefault();

    const ghost = document.createElement('div');
    ghost.textContent = item.text;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      padding: '6px 14px',
      borderRadius: '9999px',
      border: `2px solid ${categoryColor}`,
      background: itemColor,
      color: itemTextColor,
      font: `500 ${fontSize - 2}px "${fontFamily}", Inter, system-ui, sans-serif`,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
      zIndex: '99999',
      opacity: '0.95',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      willChange: 'transform',
    });
    document.body.appendChild(ghost);
    const rect = ghost.getBoundingClientRect();
    ghost.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;

    pointerDragRef.current = { itemId, ghostEl: ghost, pointerId: e.pointerId };
    setDraggedItemId(itemId);
  }, [dragEnabled, items, categoryColor, itemColor, itemTextColor, fontFamily, fontSize]);

  useEffect(() => {
    const findCategoryIdAt = (clientX, clientY) => {
      const drag = pointerDragRef.current;
      if (!drag) return null;
      const prevDisplay = drag.ghostEl.style.display;
      drag.ghostEl.style.display = 'none';
      const el = typeof document !== 'undefined' ? document.elementFromPoint(clientX, clientY) : null;
      drag.ghostEl.style.display = prevDisplay;
      const catEl = el && el.closest ? el.closest('[data-sort-category-id]') : null;
      return catEl ? catEl.getAttribute('data-sort-category-id') : null;
    };

    const cleanupDrag = () => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (drag.ghostEl && drag.ghostEl.parentNode) drag.ghostEl.parentNode.removeChild(drag.ghostEl);
      pointerDragRef.current = null;
      setDraggedItemId(null);
      setDragOverCategoryId(null);
    };

    const onMove = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      e.preventDefault();
      const rect = drag.ghostEl.getBoundingClientRect();
      drag.ghostEl.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;
      setDragOverCategoryId(findCategoryIdAt(e.clientX, e.clientY));
    };

    const onUp = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const categoryId = findCategoryIdAt(e.clientX, e.clientY);
      const itemId = drag.itemId;
      cleanupDrag();
      if (categoryId) {
        updatePlacements({ ...placements, [itemId]: categoryId });
      }
    };

    const onCancel = (e) => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (e && e.pointerId != null && e.pointerId !== drag.pointerId) return;
      cleanupDrag();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [placements, updatePlacements]);

  useEffect(() => () => {
    const drag = pointerDragRef.current;
    if (drag && drag.ghostEl && drag.ghostEl.parentNode) {
      drag.ghostEl.parentNode.removeChild(drag.ghostEl);
    }
    pointerDragRef.current = null;
  }, []);

  const handleRetry = () => {
    setPlacements({});
    onRetry?.();
  };

  const overallCorrect = perItemResults
    && items.length > 0
    && items.every((it) => perItemResults[it.id] === true);

  const renderItemPill = (item, opts = {}) => {
    const { isPlaced = false, resultValue = null } = opts;
    let bg = itemColor;
    let color = itemTextColor;
    let borderColor = categoryColor;
    if (isPlaced && showResult && resultValue !== null) {
      if (resultValue === true) {
        bg = '#dcfce7'; color = '#166534'; borderColor = '#22c55e';
      } else if (resultValue === false) {
        bg = '#fee2e2'; color = '#991b1b'; borderColor = '#ef4444';
      }
    }
    const touchDragEnabled = dragEnabled && !isPlaced;
    return (
      <span
        key={item.id + (isPlaced ? '_p' : '_pal')}
        draggable={dragEnabled && !isPlaced}
        onDragStart={(e) => startNativeDrag(e, item.id)}
        onDragEnd={handleDragEnd}
        onPointerDown={touchDragEnabled ? (e) => handlePointerDown(e, item.id) : undefined}
        onClick={isPlaced ? () => handlePlacedClick(item.id) : undefined}
        className={`
          inline-flex items-center justify-center rounded-full font-medium border-2
          whitespace-nowrap align-middle select-none
          ${dragEnabled && !isPlaced ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isPlaced && !showResult && !disabled && !isEditor ? 'cursor-pointer' : ''}
          ${draggedItemId === item.id ? 'opacity-40' : ''}
          transition-colors duration-100
        `}
        style={{
          backgroundColor: bg,
          color,
          borderColor,
          fontSize: `${fontSize - 2}px`,
          fontFamily,
          padding: '4px 12px',
          WebkitUserDrag: dragEnabled && !isPlaced ? 'element' : 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          touchAction: touchDragEnabled ? 'none' : undefined,
        }}
      >
        {item.text}
      </span>
    );
  };

  // ---- EDITOR PREVIEW ----
  if (isEditor) {
    return (
      <div
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        <div ref={scrollRef} className="flex-1 min-h-0 p-3 overflow-auto">
          {categories.length === 0 || items.length === 0 ? (
            <p className="text-sm text-gray-400 text-center">{t('games.sorting.addCategoriesInSettings')}</p>
          ) : layout === 'table' ? (
            <div className="w-full h-full overflow-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    {categories.map((cat) => (
                      <th
                        key={cat.id}
                        className="px-2 py-1.5 text-white font-semibold border border-white/40"
                        style={{
                          backgroundColor: cat.color || categoryColor,
                          fontFamily,
                          fontSize: `${fontSize - 2}px`,
                        }}
                      >
                        {cat.label || t('games.sorting.categoryFallback')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Group items by their correct category for preview
                    const rows = [];
                    const maxRows = Math.max(
                      1,
                      ...categories.map((c) => items.filter((it) => it.categoryId === c.id).length)
                    );
                    for (let r = 0; r < maxRows; r++) {
                      rows.push(
                        <tr key={r}>
                          {categories.map((cat) => {
                            const catItems = items.filter((it) => it.categoryId === cat.id);
                            const item = catItems[r];
                            return (
                              <td key={cat.id} className="px-2 py-1.5 border border-gray-200 bg-white text-center align-middle">
                                {item ? (
                                  <span
                                    className="inline-block px-2 py-0.5 rounded-full border-2"
                                    style={{
                                      backgroundColor: itemColor,
                                      color: itemTextColor,
                                      borderColor: categoryColor,
                                      fontFamily,
                                      fontSize: `${fontSize - 2}px`,
                                    }}
                                  >
                                    {item.text}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">·</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    }
                    return rows;
                  })()}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-1 justify-center">
                {items.map((item) => (
                  <span
                    key={item.id}
                    className="px-2 py-0.5 rounded-full border-2"
                    style={{
                      backgroundColor: itemColor,
                      color: itemTextColor,
                      borderColor: categoryColor,
                      fontFamily,
                      fontSize: `${fontSize - 2}px`,
                    }}
                  >
                    {item.text}
                  </span>
                ))}
              </div>
              <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(categories.length, 4)}, minmax(0, 1fr))` }}>
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className="rounded-lg border-2 border-dashed p-2 min-h-[48px] flex flex-col items-center gap-1"
                    style={{ borderColor: cat.color || categoryColor, backgroundColor: `${(cat.color || categoryColor)}0F` }}
                  >
                    <span
                      className="px-2 py-0.5 rounded-full font-semibold text-white"
                      style={{
                        backgroundColor: cat.color || categoryColor,
                        fontFamily,
                        fontSize: `${fontSize - 2}px`,
                      }}
                    >
                      {cat.label || t('games.sorting.categoryFallback')}
                    </span>
                    <div className="flex flex-wrap gap-1 justify-center">
                      {items.filter((it) => it.categoryId === cat.id).map((it) => (
                        <span
                          key={it.id}
                          className="px-2 py-0.5 rounded-full border-2 opacity-70"
                          style={{
                            backgroundColor: itemColor,
                            color: itemTextColor,
                            borderColor: categoryColor,
                            fontFamily,
                            fontSize: `${fontSize - 2}px`,
                          }}
                        >
                          {it.text}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- INTERACTIVE ----
  const paletteItems = shuffledItems.filter((it) => !placedItemIds.has(it.id));

  const renderPalette = () => (
    <div className="flex flex-wrap gap-1 justify-center items-center">
      {paletteItems.length === 0 ? (
        <span className="text-xs text-gray-400 py-1">{t('games.sorting.allSorted')}</span>
      ) : (
        paletteItems.map((item) => renderItemPill(item))
      )}
    </div>
  );

  const renderCategoryContents = (categoryId) => {
    const placedHere = shuffledItems.filter((it) => placements[it.id] === categoryId);
    if (placedHere.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1.5 justify-center">
        {placedHere.map((it) =>
          renderItemPill(it, { isPlaced: true, resultValue: perItemResults?.[it.id] ?? null })
        )}
      </div>
    );
  };

  const isPaletteHorizontal = paletteSide === 'top' || paletteSide === 'bottom';
  const paletteFirst = paletteSide === 'top' || paletteSide === 'left';

  return (
    <div
      className="w-full h-full flex flex-col rounded-xl overflow-hidden"
      style={{ backgroundColor: 'transparent' }}
    >
      {/* Board */}
      <div className="flex-1 min-h-0 p-3 overflow-auto">
        {layout === 'table' ? (
          <div className="w-full h-full flex flex-col gap-2">
            {/* Palette on top (table mode always uses top palette) */}
            {renderPalette()}
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    {categories.map((cat) => (
                      <th
                        key={cat.id}
                        className="px-2 py-2 text-white font-semibold text-sm border border-white/40"
                        style={{
                          backgroundColor: cat.color || categoryColor,
                          fontFamily,
                          fontSize: `${fontSize - 2}px`,
                        }}
                      >
                        {cat.label || t('games.sorting.categoryFallback')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {categories.map((cat) => {
                      const isOver = dragOverCategoryId === cat.id;
                      return (
                        <td
                          key={cat.id}
                          data-sort-category-id={dragEnabled ? cat.id : undefined}
                          onDragOver={(e) => handleCategoryDragOver(e, cat.id)}
                          onDragLeave={handleCategoryDragLeave}
                          onDrop={(e) => handleCategoryDrop(e, cat.id)}
                          className="align-top p-2 border border-gray-200 transition-colors"
                          style={{
                            backgroundColor: isOver ? `${(cat.color || categoryColor)}22` : '#ffffff',
                            minHeight: '80px',
                            height: '100%',
                            verticalAlign: 'top',
                          }}
                        >
                          <div className="flex flex-wrap gap-1.5 justify-center min-h-[40px]">
                            {shuffledItems
                              .filter((it) => placements[it.id] === cat.id)
                              .map((it) =>
                                renderItemPill(it, {
                                  isPlaced: true,
                                  resultValue: perItemResults?.[it.id] ?? null,
                                })
                              )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // BASKETS layout
          <div
            className={`w-full flex ${isPaletteHorizontal ? 'flex-col' : 'flex-row'} gap-2`}
          >
            {paletteFirst && renderPalette()}
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: `repeat(${Math.min(Math.max(categories.length, 1), 4)}, minmax(0, 1fr))`,
              }}
            >
              {categories.map((cat) => {
                const isOver = dragOverCategoryId === cat.id;
                const catColor = cat.color || categoryColor;
                return (
                  <div
                    key={cat.id}
                    data-sort-category-id={dragEnabled ? cat.id : undefined}
                    onDragOver={(e) => handleCategoryDragOver(e, cat.id)}
                    onDragLeave={handleCategoryDragLeave}
                    onDrop={(e) => handleCategoryDrop(e, cat.id)}
                    className="rounded-lg border-2 border-dashed p-2 min-h-[48px] flex flex-col items-center gap-1 transition-colors"
                    style={{
                      borderColor: catColor,
                      backgroundColor: isOver ? `${catColor}22` : `${catColor}0D`,
                    }}
                  >
                    <span
                      className="px-2 py-0.5 rounded-full font-semibold text-white"
                      style={{
                        backgroundColor: catColor,
                        fontFamily,
                        fontSize: `${fontSize - 2}px`,
                      }}
                    >
                      {cat.label || t('games.sorting.categoryFallback')}
                    </span>
                    <div className="w-full flex flex-wrap gap-1 justify-center">
                      {renderCategoryContents(cat.id)}
                    </div>
                  </div>
                );
              })}
            </div>
            {!paletteFirst && renderPalette()}
          </div>
        )}
      </div>

      {showResult && !overallCorrect && allowRetry && (
        <div className="px-3 py-2 flex items-center justify-end">
          <button
            onClick={handleRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
          >
            <RotateCcw size={14} />
            {t('games.common.tryAgain')}
          </button>
        </div>
      )}
    </div>
  );
};

export const validateSorting = (items = [], placements = {}) => {
  const perItemResults = {};
  let correctCount = 0;
  items.forEach((it) => {
    const placed = placements?.[it.id];
    const isCorrect = placed === it.categoryId;
    perItemResults[it.id] = isCorrect;
    if (isCorrect) correctCount += 1;
  });
  return {
    perItemResults,
    correctCount,
    total: items.length,
    isCorrect: items.length > 0 && correctCount === items.length,
  };
};

export default SortingElement;
